import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronDown, Building2, Globe, Plus, Pencil, Trash2, X, Factory,
  CheckCircle, XCircle, Users, Search, Filter, AlertCircle, Code2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Filter options ─── */
const FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'internal', label: 'Intern' },
  { value: 'external', label: 'Extern' },
  { value: 'pending', label: 'Ausstehend' },
];

/* ─── Pending Registrations Section ─── */
const PendingRegistrations = ({ companies, onApprove, t }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [companyUsers, setCompanyUsers] = useState({});

  const pending = companies.filter(c => c.approved_status === 'pending');
  if (pending.length === 0) return null;

  const loadUsers = async (companyId) => {
    if (expandedId === companyId) { setExpandedId(null); return; }
    setExpandedId(companyId);
    if (!companyUsers[companyId]) {
      try {
        const res = await axios.get(`${API}/api/external-companies/${companyId}/users`);
        setCompanyUsers(prev => ({ ...prev, [companyId]: res.data }));
      } catch { setCompanyUsers(prev => ({ ...prev, [companyId]: [] })); }
    }
  };

  return (
    <div className="mb-6" data-testid="pending-registrations">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          Ausstehende Registrierungen ({pending.length})
        </h3>
      </div>
      <div className="space-y-2">
        {pending.map(c => (
          <div
            key={c.company_id}
            className="border border-amber-200 dark:border-amber-800/40 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 overflow-hidden"
            data-testid={`pending-company-${c.company_id}`}
          >
            <div className="flex items-center justify-between p-3 gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadUsers(c.company_id)}>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.registration_type === 'group' ? (
                    <Building2 className="h-4 w-4 text-orange-500 shrink-0" />
                  ) : (
                    <Factory className="h-4 w-4 text-teal-500 shrink-0" />
                  )}
                  <span className="font-semibold text-sm">{c.company_name}</span>
                  <Badge className={
                    c.registration_type === 'group'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-xs'
                      : 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 text-xs'
                  } data-testid={`pending-type-${c.company_id}`}>
                    {c.registration_type === 'group' ? 'Gruppe' : 'Einzelwerk'}
                  </Badge>
                  {c.vat_id_optional && <span className="text-xs text-muted-foreground">VAT: {c.vat_id_optional}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{c.contact_name} &middot; {c.contact_email}</p>
                <p className="text-xs text-muted-foreground">{c.street} {c.house_number}, {c.postal_code} {c.city}, {c.country}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" className="bg-green-500 hover:bg-green-600 gap-1 h-8 text-xs" onClick={() => onApprove(c.company_id, 'approved')} data-testid={`approve-company-btn-${c.company_id}`}>
                  <CheckCircle className="h-3.5 w-3.5" />{t('admin.approveCompany')}
                </Button>
                <Button size="sm" variant="outline" className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 h-8 text-xs" onClick={() => onApprove(c.company_id, 'rejected')} data-testid={`reject-company-btn-${c.company_id}`}>
                  <XCircle className="h-3.5 w-3.5" />{t('admin.rejectCompany')}
                </Button>
              </div>
            </div>
            {expandedId === c.company_id && (
              <div className="px-3 pb-3 border-t border-amber-200/50 dark:border-amber-800/30 bg-amber-50/30 dark:bg-amber-950/10">
                <p className="text-xs font-medium text-muted-foreground py-2">{t('admin.org.linkedUsers')}</p>
                {(companyUsers[c.company_id] || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('admin.org.noLinkedUsers')}</p>
                ) : (
                  <div className="space-y-1">
                    {companyUsers[c.company_id].map(u => (
                      <div key={u.user_id} className="flex items-center gap-2 text-xs">
                        <Users className="h-3 w-3" />
                        <span className="font-medium">{u.name}</span>
                        <span className="text-muted-foreground">{u.email}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5">{u.approved ? 'aktiv' : 'inaktiv'}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Rejected Companies Section ─── */
const RejectedCompanies = ({ companies, onApprove, t }) => {
  const rejected = companies.filter(c => c.approved_status === 'rejected');
  if (rejected.length === 0) return null;

  return (
    <div className="mb-6" data-testid="rejected-registrations">
      <div className="flex items-center gap-2 mb-3">
        <XCircle className="h-4 w-4 text-red-400" />
        <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">
          Abgelehnte Registrierungen ({rejected.length})
        </h3>
      </div>
      <div className="space-y-2">
        {rejected.map(c => (
          <div
            key={c.company_id}
            className="border border-red-200/60 dark:border-red-800/30 rounded-lg bg-red-50/30 dark:bg-red-950/10 overflow-hidden"
            data-testid={`rejected-company-${c.company_id}`}
          >
            <div className="flex items-center justify-between p-3 gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.registration_type === 'group' ? (
                    <Building2 className="h-4 w-4 text-orange-500 shrink-0" />
                  ) : (
                    <Factory className="h-4 w-4 text-teal-500 shrink-0" />
                  )}
                  <span className="font-semibold text-sm">{c.company_name}</span>
                  <Badge className={
                    c.registration_type === 'group'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-xs'
                      : 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 text-xs'
                  }>
                    {c.registration_type === 'group' ? 'Gruppe' : 'Einzelwerk'}
                  </Badge>
                  <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs">Abgelehnt</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{c.contact_name} &middot; {c.contact_email}</p>
              </div>
              <Button size="sm" className="bg-green-500 hover:bg-green-600 gap-1 h-8 text-xs shrink-0" onClick={() => onApprove(c.company_id, 'approved')} data-testid={`reapprove-company-btn-${c.company_id}`}>
                <CheckCircle className="h-3.5 w-3.5" />Doch genehmigen
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Org Tree View ─── */
const OrgTreeView = ({ groups, sites, onEditGroup, onDeleteGroup, onEditSite, onDeleteSite, onAddSite, t }) => {
  const [collapsed, setCollapsed] = useState(new Set());

  const toggle = (id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sitesMap = useMemo(() => {
    const map = {};
    sites.forEach(s => {
      if (!map[s.group_id]) map[s.group_id] = [];
      map[s.group_id].push(s);
    });
    return map;
  }, [sites]);

  return (
    <div className="space-y-0.5" data-testid="org-tree">
      {groups.map(group => {
        const groupSites = sitesMap[group.group_id] || [];
        const isCollapsed = collapsed.has(group.group_id);
        const isStandalone = group.is_standalone_site;
        const isExternal = group.is_external;

        if (isStandalone) {
          const site = groupSites[0];
          return (
            <div key={group.group_id}>
              <div
                className="flex items-center gap-1.5 py-2 px-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 group transition-colors"
                data-testid={`org-standalone-${group.group_id}`}
              >
                <span className="w-5 h-5 flex items-center justify-center shrink-0" />
                <Factory className="h-4 w-4 text-teal-500 shrink-0" />
                <span className="flex-1 text-sm font-semibold truncate">{group.group_name}</span>
                <Badge className="bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 text-xs shrink-0">Einzelwerk</Badge>
                {isExternal && (
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 text-xs shrink-0">Extern</Badge>
                )}
                {site && <span className="text-xs text-muted-foreground shrink-0">{site.city}, {site.country}</span>}
                {(site?.api_docs_enabled === false || group.api_docs_enabled === false) && (
                  <Badge
                    className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-xs shrink-0 gap-1"
                    data-testid={`api-docs-off-standalone-${group.group_id}`}
                    title="API-/Schnittstellen-Doku deaktiviert"
                  >
                    <Code2 className="h-3 w-3" /> API-Doku aus
                  </Badge>
                )}
                <div className={`w-2 h-2 rounded-full shrink-0 ${group.active ? 'bg-green-500' : 'bg-red-500'}`} />
                <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditGroup(group)} title={t('admin.org.editTitle')} data-testid={`edit-group-${group.group_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onDeleteGroup(group.group_id)} title={t('admin.org.deleteTitle')} data-testid={`delete-group-${group.group_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={group.group_id}>
            <div
              className="flex items-center gap-1.5 py-2 px-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 group transition-colors"
              data-testid={`org-group-${group.group_id}`}
            >
              <button onClick={() => toggle(group.group_id)} className="w-5 h-5 flex items-center justify-center shrink-0">
                {groupSites.length > 0 ? (
                  isCollapsed ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />
                ) : <span className="w-4" />}
              </button>
              <Building2 className="h-4 w-4 text-orange-500 shrink-0" />
              <span className="flex-1 text-sm font-semibold truncate">{group.group_name}</span>
              <Badge variant="outline" className="text-xs shrink-0">{groupSites.length} Sites</Badge>
              {isExternal && (
                <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 text-xs shrink-0">Extern</Badge>
              )}
              {group.domains_allowed?.length > 0 && (
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs shrink-0">
                  {group.domains_allowed.map(d => `@${d.replace(/^@/, '')}`).join(', ')}
                </Badge>
              )}
              {group.api_docs_enabled === false && (
                <Badge
                  className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-xs shrink-0 gap-1"
                  data-testid={`api-docs-off-group-${group.group_id}`}
                  title="API-/Schnittstellen-Doku für diese Gruppe deaktiviert"
                >
                  <Code2 className="h-3 w-3" /> API-Doku aus
                </Badge>
              )}
              <div className={`w-2 h-2 rounded-full shrink-0 ${group.active ? 'bg-green-500' : 'bg-red-500'}`} />
              <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onAddSite(group.group_id)} title={t('admin.org.addSiteTitle')} data-testid={`add-site-to-group-${group.group_id}`}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditGroup(group)} title={t('admin.org.editTitle')} data-testid={`edit-group-${group.group_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onDeleteGroup(group.group_id)} title={t('admin.org.deleteTitle')} data-testid={`delete-group-${group.group_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {!isCollapsed && groupSites.map(site => (
              <div
                key={site.site_id}
                className="flex items-center gap-1.5 py-1.5 px-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 group transition-colors"
                style={{ paddingLeft: '36px' }}
                data-testid={`org-site-${site.site_id}`}
              >
                <Globe className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="flex-1 text-sm font-medium truncate">{site.site_name}</span>
                {site.fsc_certified && (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs shrink-0" data-testid={`fsc-badge-${site.site_id}`}>FSC</Badge>
                )}
                {site.api_docs_enabled === false && (
                  <Badge
                    className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-xs shrink-0 gap-1"
                    data-testid={`api-docs-off-site-${site.site_id}`}
                    title="API-/Schnittstellen-Doku für diese Site deaktiviert"
                  >
                    <Code2 className="h-3 w-3" /> API-Doku aus
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground shrink-0">{site.city}, {site.country}</span>
                <div className={`w-2 h-2 rounded-full shrink-0 ${site.active ? 'bg-green-500' : 'bg-red-500'}`} />
                <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditSite(site)} title={t('admin.org.editTitle')} data-testid={`edit-site-${site.site_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onDeleteSite(site.site_id)} title={t('admin.org.deleteTitle')} data-testid={`delete-site-${site.site_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Group Edit Form ─── */
const GroupEditForm = ({ initialData, onSubmit, onCancel, t }) => {
  const [formData, setFormData] = useState({
    group_name: initialData?.group_name || '',
    domains_allowed: initialData?.domains_allowed || [],
    active: initialData?.active ?? true,
    api_docs_enabled: initialData?.api_docs_enabled ?? true
  });
  const [domainInput, setDomainInput] = useState('');

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (d && !formData.domains_allowed.includes(d)) {
      setFormData(prev => ({ ...prev, domains_allowed: [...prev.domains_allowed, d] }));
    }
    setDomainInput('');
  };

  const removeDomain = (domain) => {
    setFormData(prev => ({ ...prev, domains_allowed: prev.domains_allowed.filter(d => d !== domain) }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <div className="space-y-2">
        <Label>{t('admin.groupName')}</Label>
        <Input value={formData.group_name} onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))} required data-testid="group-name-input" />
      </div>
      <div className="space-y-2">
        <Label>E-Mail-Domains</Label>
        <div className="flex gap-2">
          <Input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="z.B. firma.de"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
            data-testid="domain-input"
          />
          <Button type="button" variant="outline" onClick={addDomain} data-testid="add-domain-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {formData.domains_allowed.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {formData.domains_allowed.map(d => (
              <Badge key={d} className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 gap-1">
                @{d}
                <button type="button" onClick={() => removeDomain(d)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('admin.org.autoLinkEmailHint')}</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="group-active" checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} />
        <Label htmlFor="group-active">Aktiv</Label>
      </div>
      <div className="border rounded-md p-3 bg-orange-50/40 dark:bg-orange-950/20">
        <label className="flex items-start gap-2 text-sm cursor-pointer" htmlFor="group-api-docs">
          <input
            type="checkbox"
            id="group-api-docs"
            className="mt-0.5"
            checked={formData.api_docs_enabled}
            onChange={(e) => setFormData(prev => ({ ...prev, api_docs_enabled: e.target.checked }))}
            data-testid="group-api-docs-toggle"
          />
          <div>
            <span className="font-medium">API- & Schnittstellen-Dokumentation freigeben</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              Aktiviert die API-Doku (Swagger, SDKs, Beispiele) für alle Sites dieser Gruppe im Seller-Portal.
              Deaktivieren blendet die Dokumentation gruppenweit aus.
            </p>
          </div>
        </label>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="save-group-btn">
          {initialData ? t('common.save') : t('common.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};

/* ─── Site Edit Form ─── */
const SiteEditForm = ({ initialData, groups, onSubmit, onCancel, t }) => {
  const [formData, setFormData] = useState({
    group_id: initialData?.group_id || '',
    site_name: initialData?.site_name || '',
    street: initialData?.street || '',
    house_number: initialData?.house_number || '',
    postal_code: initialData?.postal_code || '',
    city: initialData?.city || '',
    country: initialData?.country || '',
    currency: initialData?.currency || 'EUR',
    fsc_certified: initialData?.fsc_certified ?? false,
    active: initialData?.active ?? true,
    api_docs_enabled: initialData?.api_docs_enabled ?? true
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <div className="space-y-2">
        <Label>{t('admin.orgTitle')}</Label>
        <Select value={formData.group_id} onValueChange={(v) => setFormData(prev => ({ ...prev, group_id: v }))}>
          <SelectTrigger><SelectValue placeholder={t('admin.org.chooseGroup')} /></SelectTrigger>
          <SelectContent>{groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Site-Name</Label>
        <Input value={formData.site_name} onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))} required data-testid="site-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>{t('admin.street')}</Label><Input value={formData.street} onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))} required /></div>
        <div className="space-y-2"><Label>{t('admin.houseNumber')}</Label><Input value={formData.house_number} onChange={(e) => setFormData(prev => ({ ...prev, house_number: e.target.value }))} required /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>{t('admin.postalCode')}</Label><Input value={formData.postal_code} onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))} required /></div>
        <div className="space-y-2"><Label>{t('admin.city')}</Label><Input value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} required /></div>
      </div>
      <div className="space-y-2"><Label>{t('admin.country')}</Label><Input value={formData.country} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} required /></div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="site-fsc" checked={formData.fsc_certified} onChange={(e) => setFormData(prev => ({ ...prev, fsc_certified: e.target.checked }))} data-testid="site-fsc-checkbox" />
        <Label htmlFor="site-fsc">FSC-zertifiziert</Label>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="site-active" checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} />
        <Label htmlFor="site-active">Aktiv</Label>
      </div>
      <div className="border rounded-md p-3 bg-orange-50/40 dark:bg-orange-950/20">
        <label className="flex items-start gap-2 text-sm cursor-pointer" htmlFor="site-api-docs">
          <input
            type="checkbox"
            id="site-api-docs"
            className="mt-0.5"
            checked={formData.api_docs_enabled}
            onChange={(e) => setFormData(prev => ({ ...prev, api_docs_enabled: e.target.checked }))}
            data-testid="site-api-docs-toggle"
          />
          <div>
            <span className="font-medium">API- & Schnittstellen-Dokumentation freigeben</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              Blendet Swagger-UI, SDKs & Endpoint-Referenz im Seller-Portal dieser Site ein.
              Wirkt nur, wenn auch die übergeordnete Gruppe die Doku freigegeben hat.
            </p>
          </div>
        </label>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="save-site-btn">
          {initialData ? t('common.save') : t('common.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};

/* ─── Main Unified OrgTreeTab ─── */
export const OrgTreeTab = ({
  groups, sites,
  onGroupCreated, onSiteCreated, onGroupUpdated, onSiteUpdated, onGroupDeleted, onSiteDeleted,
  externalCompanies = [], onApproveCompany, onAddCompany,
  t
}) => {
  const [groupDialog, setGroupDialog] = useState({ open: false, editData: null });
  const [siteDialog, setSiteDialog] = useState({ open: false, editData: null, presetGroupId: null });
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const pendingCount = externalCompanies.filter(c => c.approved_status === 'pending').length;
  const rejectedCount = externalCompanies.filter(c => c.approved_status === 'rejected').length;

  const filteredGroups = useMemo(() => {
    let result = groups;

    // Apply type filter
    if (activeFilter === 'internal') {
      result = result.filter(g => !g.is_external);
    } else if (activeFilter === 'external') {
      result = result.filter(g => g.is_external);
    } else if (activeFilter === 'pending') {
      result = []; // In pending mode, we only show pending registrations
    }

    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchingSiteGroupIds = new Set(
        sites.filter(s => s.site_name?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q))
          .map(s => s.group_id)
      );
      result = result.filter(g =>
        g.group_name?.toLowerCase().includes(q) || matchingSiteGroupIds.has(g.group_id)
      );
    }

    return result;
  }, [groups, sites, activeFilter, searchQuery]);

  const internalCount = groups.filter(g => !g.is_external).length;
  const externalCount = groups.filter(g => g.is_external).length;

  const handleGroupSubmit = async (data) => {
    if (groupDialog.editData) {
      await onGroupUpdated(groupDialog.editData.group_id, data);
    } else {
      await onGroupCreated(data);
    }
    setGroupDialog({ open: false, editData: null });
  };

  const handleSiteSubmit = async (data) => {
    const submitData = siteDialog.presetGroupId && !data.group_id ? { ...data, group_id: siteDialog.presetGroupId } : data;
    if (siteDialog.editData) {
      await onSiteUpdated(siteDialog.editData.site_id, submitData);
    } else {
      await onSiteCreated(submitData);
    }
    setSiteDialog({ open: false, editData: null, presetGroupId: null });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Organisationsstruktur</CardTitle>
              <CardDescription>{t('admin.org.manageTitle')}</CardDescription>
            </div>
            <div className="flex gap-2 shrink-0">
              {onAddCompany && (
                <Button variant="outline" onClick={onAddCompany} data-testid="add-company-btn">
                  <Factory className="h-4 w-4 mr-2" />Ext. Firma
                </Button>
              )}
              <Button variant="outline" onClick={() => setSiteDialog({ open: true, editData: null, presetGroupId: null })} disabled={groups.length === 0} data-testid="add-site-btn">
                <Globe className="h-4 w-4 mr-2" />Site
              </Button>
              <Button onClick={() => setGroupDialog({ open: true, editData: null })} data-testid="add-group-btn">
                <Plus className="h-4 w-4 mr-2" />Gruppe
              </Button>
            </div>
          </div>

          {/* KPI Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="rounded-lg border bg-card p-3 text-center">
              <p className="text-2xl font-bold">{groups.length}</p>
              <p className="text-xs text-muted-foreground">Gruppen</p>
            </div>
            <div className="rounded-lg border bg-card p-3 text-center">
              <p className="text-2xl font-bold">{sites.length}</p>
              <p className="text-xs text-muted-foreground">Sites</p>
            </div>
            <div className="rounded-lg border bg-card p-3 text-center">
              <p className="text-2xl font-bold">{externalCount}</p>
              <p className="text-xs text-muted-foreground">Extern</p>
            </div>
            <div className={`rounded-lg border p-3 text-center ${pendingCount > 0 ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/20' : 'bg-card'}`}>
              <p className={`text-2xl font-bold ${pendingCount > 0 ? 'text-amber-600' : ''}`}>{pendingCount}</p>
              <p className="text-xs text-muted-foreground">Ausstehend</p>
            </div>
          </div>

          {/* Search + Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('admin.org.searchGroups')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="org-search-input"
              />
            </div>
            <div className="flex gap-1" data-testid="org-filter-bar">
              {FILTERS.map(f => (
                <Button
                  key={f.value}
                  size="sm"
                  variant={activeFilter === f.value ? 'default' : 'outline'}
                  className={`text-xs h-9 ${activeFilter === f.value ? '' : ''}`}
                  onClick={() => setActiveFilter(f.value)}
                  data-testid={`org-filter-${f.value}`}
                >
                  {f.label}
                  {f.value === 'pending' && pendingCount > 0 && (
                    <Badge className="ml-1 h-4 min-w-[16px] px-1 text-[10px] bg-amber-500">{pendingCount}</Badge>
                  )}
                  {f.value === 'internal' && <span className="ml-1 text-muted-foreground">({internalCount})</span>}
                  {f.value === 'external' && <span className="ml-1 text-muted-foreground">({externalCount})</span>}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Pending Registrations */}
          {(activeFilter === 'all' || activeFilter === 'pending') && externalCompanies.length > 0 && onApproveCompany && (
            <PendingRegistrations companies={externalCompanies} onApprove={onApproveCompany} t={t} />
          )}

          {/* Rejected (only when viewing all or pending) */}
          {(activeFilter === 'all' || activeFilter === 'pending') && externalCompanies.length > 0 && onApproveCompany && (
            <RejectedCompanies companies={externalCompanies} onApprove={onApproveCompany} t={t} />
          )}

          {/* Tree View */}
          {activeFilter !== 'pending' && (
            filteredGroups.length > 0 ? (
              <OrgTreeView
                groups={filteredGroups}
                sites={sites}
                onEditGroup={(g) => setGroupDialog({ open: true, editData: g })}
                onDeleteGroup={onGroupDeleted}
                onEditSite={(s) => setSiteDialog({ open: true, editData: s, presetGroupId: null })}
                onDeleteSite={onSiteDeleted}
                onAddSite={(gid) => setSiteDialog({ open: true, editData: null, presetGroupId: gid })}
                t={t}
              />
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {searchQuery ? 'Keine Ergebnisse gefunden.' : 'Keine Gruppen vorhanden. Erstellen Sie eine Gruppe, um zu beginnen.'}
              </p>
            )
          )}

          {activeFilter === 'pending' && pendingCount === 0 && rejectedCount === 0 && (
            <p className="text-muted-foreground text-center py-8">{t('admin.org.noPendingRegs')}</p>
          )}
        </CardContent>
      </Card>

      {/* Group Dialog */}
      <Dialog open={groupDialog.open} onOpenChange={(open) => { if (!open) setGroupDialog({ open: false, editData: null }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{groupDialog.editData ? 'Gruppe bearbeiten' : t('admin.addGroup')}</DialogTitle>
            <DialogDescription>Gruppen-Stammdaten inkl. E-Mail-Domains konfigurieren.</DialogDescription>
          </DialogHeader>
          <GroupEditForm
            initialData={groupDialog.editData}
            onSubmit={handleGroupSubmit}
            onCancel={() => setGroupDialog({ open: false, editData: null })}
            t={t}
          />
        </DialogContent>
      </Dialog>

      {/* Site Dialog */}
      <Dialog open={siteDialog.open} onOpenChange={(open) => { if (!open) setSiteDialog({ open: false, editData: null, presetGroupId: null }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{siteDialog.editData ? 'Site bearbeiten' : t('admin.addSite')}</DialogTitle>
            <DialogDescription>{t('admin.sitesDesc')}</DialogDescription>
          </DialogHeader>
          <SiteEditForm
            initialData={siteDialog.editData ? siteDialog.editData : siteDialog.presetGroupId ? { group_id: siteDialog.presetGroupId } : null}
            groups={groups}
            onSubmit={handleSiteSubmit}
            onCancel={() => setSiteDialog({ open: false, editData: null, presetGroupId: null })}
            t={t}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
